import React, { lazy, Suspense } from "react";
import { createBrowserRouter } from "react-router-dom";
import Layout from "./components/layout";
import AuthGuard from "./guards/AuthGuard";
import OrganisationGuard from "./guards/OrganisationGuard";
import RoleGuard, { AllowedRole } from "./guards/RoleGuard";
import SubscriptionGuard from "./guards/SubscriptionGuard";
import Page404 from "./rootAuth/404page";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { recaptchaSiteKey } from "./Environment";
import { PlanValidationProvider } from "@/context/paymentContext";
import { RouteErrorFallback, ErrorFallback } from "./components/common/ErrorFallback";
import { ErrorBoundary } from "react-error-boundary";
import {
  UserRoleEnumValue,
  PortfolioRoleEnumValue,
  ProgramRoleEnumValue
} from "@backend/src/schemas/enums";

// ── Shared Role Configurations ──
const ADMIN_ONLY_ROLES: AllowedRole[] = [UserRoleEnumValue.ADMINISTRATOR];
const MANAGER_ROLES: AllowedRole[] = [
  UserRoleEnumValue.ADMINISTRATOR,
  UserRoleEnumValue.PROJECT_MANAGER,
  PortfolioRoleEnumValue.PORTFOLIO_MANAGER,
  ProgramRoleEnumValue.PROGRAM_MANAGER,
];
const GENERAL_SETTINGS_ROLES: AllowedRole[] = [
  UserRoleEnumValue.ADMINISTRATOR,
  UserRoleEnumValue.PROJECT_MANAGER,
  PortfolioRoleEnumValue.PORTFOLIO_MANAGER,
  ProgramRoleEnumValue.PROGRAM_MANAGER,
];
const ORG_DETAILS_ROLES: AllowedRole[] = [
  UserRoleEnumValue.ADMINISTRATOR,
  UserRoleEnumValue.PROJECT_MANAGER,
  PortfolioRoleEnumValue.PORTFOLIO_MANAGER,
  ProgramRoleEnumValue.PROGRAM_MANAGER,
  UserRoleEnumValue.TEAM_MEMBER,
];
const NON_COLLABORATOR_ROLES: AllowedRole[] = [
  UserRoleEnumValue.COLLABORATOR,
];

// Statically import pages using smart-webcomponents-react to prevent dynamic element layout issues
import ProjectsList from "./pages/projectsList";
import TaskViews from "./components/views/";
import MyTasks from "./pages/tasks/MyTask";

// Lazy-loaded Components for all other routes
const SuspenseLoader = () => (
  <div className="flex h-screen w-screen items-center justify-center bg-slate-50/50">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-indigo-600"></div>
  </div>
);

/**
 * Retries a dynamic import with a cache-busting query parameter.
 * This handles the "Failed to fetch dynamically imported module" error
 * that occurs when a new deployment replaces old hashed chunk files
 * while users still have the old index.html cached.
 */
const retryDynamicImport = (
  importFunc: () => Promise<{ default: React.ComponentType<any> }>,
  retries = 1
): Promise<{ default: React.ComponentType<any> }> => {
  return importFunc().catch((error) => {
    if (retries > 0) {
      return new Promise<{ default: React.ComponentType<any> }>((resolve) => {
        setTimeout(() => resolve(retryDynamicImport(importFunc, retries - 1)), 1000);
      });
    }
    const reloadKey = "chunk-reload-" + window.location.pathname;
    const lastReload = sessionStorage.getItem(reloadKey);
    const now = Date.now();

    if (!lastReload || now - parseInt(lastReload, 10) > 10000) {
      sessionStorage.setItem(reloadKey, now.toString());
      window.location.reload();
      return new Promise<{ default: React.ComponentType<any> }>(() => { });
    }

    throw error;
  });
};

const lazyLoad = (importFunc: () => Promise<{ default: React.ComponentType<any> }>) => {
  const Component = lazy(() => retryDynamicImport(importFunc));
  return (props: any) => (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );
};

const Organisation = lazyLoad(() => import("./pages/organisation"));
const OrganisationDetails = lazyLoad(() => import("./pages/organisation/OrganisationDetails"));
const OrganisationRiskSettings = lazyLoad(() => import("./pages/organisation/OrganisationRiskSettings"));
const OrganisationNonWorkingDays = lazyLoad(() => import("./pages/organisation/OrganisationNonWorkingDays"));
const OrganisationKpiSettings = lazyLoad(() => import("./pages/organisation/OrganisationKpiSettings"));
const CostSettings = lazyLoad(() => import("./pages/configuration/costSettings/CostSettings"));
const DefaultTimesheetLines = lazyLoad(() => import("./pages/organisation/DefaultTimesheetLines"));
const Verification = lazyLoad(() => import("./pages/authentication/verification"));
const ForgotPassword = lazyLoad(() => import("./pages/authentication/forgot-password"));
const ResetPassword = lazyLoad(() => import("./pages/authentication/forgot-password/reset-password"));
const DashboardNew = lazyLoad(() => import("./pages/dashboard/DashboardNew"));
const ProjectMember = lazyLoad(() => import("./pages/project-details/projectMember"));
const Terms = lazyLoad(() => import("./pages/terms/terms"));
const PrivacyPolicy = lazyLoad(() => import("./pages/terms/privacyPolicy"));
const ProjectDetails = lazyLoad(() => import("./pages/project-details/ProjectDetails"));
const NewAccountSettings = lazyLoad(() => import("./pages/account-settings/NewAccount"));
const NewLogin = lazyLoad(() => import("./pages/authentication/login/newLogin"));
const CheckoutForm = lazyLoad(() => import("./pages/payment-checkout"));
const RiskManagement = lazyLoad(() => import("./pages/risk-management"));
const Scales = lazyLoad(() => import("./pages/risk-management/scales"));
const PaymentPlans = lazyLoad(() => import("./components/PaymentPlans"));
const ManagerDashboard = lazyLoad(() => import("./pages/dashboard/managerDashboard"));
const ApiDocumentation = lazyLoad(() => import("./components/views/openApidocs"));
const CollaboratorVerification = lazyLoad(() => import("./pages/authentication/verification/varifyCollaborator"));
const ResourceUtilization = lazyLoad(() => import("./pages/projectsList/resourceUtilization"));
const CapacityPlanning = lazyLoad(() => import("./pages/capacity-planning/CapacityPlanning"));
const AppClint = lazyLoad(() => import("./pages/organisation/appClinte"));
const ManageAbsence = lazyLoad(() => import("./pages/absence").then(module => ({ default: module.ManageAbsence })));
const MyTimesheet = lazyLoad(() => import("./pages/tasks/MyTimesheet"));
const TimesheetApprovals = lazyLoad(() => import("./pages/tasks/TimesheetApprovals"));
const CraDashboard = lazyLoad(() => import("./pages/tasks/CraDashboard"));
const ProjectBudget = lazyLoad(() => import("./pages/budget/ProjectBudget"));
const BudgetPerformance = lazyLoad(() => import("./pages/dashboard/budgetPerformance"));
const ResourceAdministration = lazyLoad(() => import("./pages/organisation/ResourceAdministration").then(module => ({ default: module.ResourceAdministration })));
const UserProfile = lazyLoad(() => import("./pages/organisation/UserProfile"));
const UserDocumentation = lazyLoad(() => import("./pages/docs/UserDocumentationPage"));
const DeliverablesList = lazyLoad(() => import("./pages/deliverables/DeliverablesList").then(module => ({ default: module.DeliverablesList })));
const MyDeliverables = lazyLoad(() => import("./pages/deliverables/MyDeliverables").then(module => ({ default: module.MyDeliverables })));
const CreateDeliverable = lazyLoad(() => import("./pages/deliverables/CreateDeliverable").then(module => ({ default: module.CreateDeliverable })));
const DeliverableDetail = lazyLoad(() => import("./pages/deliverables/DeliverableDetail").then(module => ({ default: module.DeliverableDetail })));

export const LayoutWithValidationProvider = ({
  children,
}: {
  children?: React.ReactNode;
}) => (
  <PlanValidationProvider>
    <Layout>{children}</Layout>
  </PlanValidationProvider>
);

const AuthenticatedRouteErrorFallback = () => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <LayoutWithValidationProvider>
      <RouteErrorFallback standalone={false} />
    </LayoutWithValidationProvider>
  </ErrorBoundary>
);

export const router = createBrowserRouter([
  {
    path: "/login",
    element: (
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <NewLogin />
      </GoogleReCaptchaProvider>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/signup",
    element: (
      <GoogleReCaptchaProvider
        reCaptchaKey={recaptchaSiteKey}
        scriptProps={{
          async: true,
          defer: true,
          appendTo: "head",
        }}
      >
        <NewLogin />
      </GoogleReCaptchaProvider>
    ),
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/term-and-condition",
    element: <Terms />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/privacy-policy",
    element: <PrivacyPolicy />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/docs",
    element: <UserDocumentation />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/docs/en",
    element: <UserDocumentation />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/docs/fr",
    element: <UserDocumentation />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/verify-email",
    element: <Verification />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/collaborator/:userId",
    element: <CollaboratorVerification />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <LayoutWithValidationProvider />
      </AuthGuard>
    ),
    errorElement: <AuthenticatedRouteErrorFallback />,
    children: [
      {
        errorElement: <RouteErrorFallback standalone={false} />,
        children: [
          // ── Routes accessible to ALL authenticated users ──
      {
        path: "/organisation",
        element: <Organisation />,
      },
      {
        path: "/documentation",
        element: <UserDocumentation />,
      },
      {
        path: "/",
        element: <RoleGuard disallowedRoles={NON_COLLABORATOR_ROLES} redirectTo="/mytasks" />,
        children: [
          {
            path: "/",
            element: <ManagerDashboard />,
          },
        ],
      },
      {
        path: "/account-settings",
        element: <NewAccountSettings />,
      },

      {
        /* Strictly Admin-only configuration and billing routes */
        element: <RoleGuard allowedRoles={ADMIN_ONLY_ROLES} redirectTo="/projects" />,
        children: [
          {
            path: "/plans",
            element: <PaymentPlans onClose={() => { }} />,
          },
          {
            path: "/checkout/:client_secret",
            element: <CheckoutForm />,
          },
          {
            path: "/configuration/:organisationId/cost-settings",
            element: <CostSettings />,
          },
          {
            path: "/organisation/:organisationId/api-integration",
            element: <AppClint />,
          },
        ],
      },

      {
        /* Organization profile & Non-working days: accessible to Admin, PfM, PgM, PM, Member (read-only for non-admin) */
        element: <RoleGuard allowedRoles={ORG_DETAILS_ROLES} redirectTo="/projects" />,
        children: [
          {
            path: "/organisation/:organisationId/organization-details",
            element: <OrganisationDetails />,
          },
          {
            path: "/organisation/:organisationId/nonworking-days",
            element: <OrganisationNonWorkingDays />,
          },
        ],
      },

      {
        /* General management settings: accessible to Admin, PfM, PgM, PM */
        element: <RoleGuard allowedRoles={GENERAL_SETTINGS_ROLES} redirectTo="/projects" />,
        children: [
          {
            path: "/organisation/:organisationId/risk-settings",
            element: <OrganisationRiskSettings />,
          },
          {
            path: "/organisation/:organisationId/kpi-settings",
            element: <OrganisationKpiSettings />,
          },
          {
            path: "/organisation/:organisationId/project-settings",
            element: <OrganisationDetails />,
          },
          {
            path: "/organisation/:organisationId/job-titles",
            element: <OrganisationDetails />,
          },
          {
            path: "/organisation/:organisationId/resource-administration",
            element: <ResourceAdministration />,
          },
          {
            path: "/organisation/:organisationId/organization-member",
            element: <OrganisationDetails />,
          },
          {
            path: "/organisation/:organisationId/user-profile/:userOrganisationId",
            element: <UserProfile />,
          },
          {
            path: "/organisation/:organisationId/manage-absence",
            element: <ManageAbsence />,
          },
          {
            path: "/organisation/:organisationId/timesheet-settings",
            element: <DefaultTimesheetLines />,
          },
        ],
      },

      {
        element: <SubscriptionGuard feature="paid" />,
        children: [
          {
            element: <RoleGuard disallowedRoles={NON_COLLABORATOR_ROLES} redirectTo="/mytasks" />,
            children: [
              {
                path: "/projectDashboard/:projectId",
                element: <DashboardNew />,
              },
            ],
          },
          {
            element: <RoleGuard allowedRoles={MANAGER_ROLES} redirectTo="/projects" />,
            children: [
              {
                path: "/resource-management/:organisationId/resource-utilization",
                element: <ResourceUtilization />,
              },
              {
                path: "/resource-management/:organisationId/capacity-planning",
                element: <CapacityPlanning />,
              },
            ],
          },
        ],
      },

      {
        path: "/",
        element: <OrganisationGuard />,
        children: [
          {
            /* Project Roadmap & Hierarchy List: accessible to all except Collaborators */
            element: <RoleGuard disallowedRoles={NON_COLLABORATOR_ROLES} redirectTo="/mytasks" />,
            children: [
              {
                path: "/projects",
                element: <ProjectsList />,
              },
              {
                path: "/project-details/:id",
                element: <ProjectDetails />,
              },
              {
                path: "/members/:projectId",
                element: <ProjectMember />,
              },
            ],
          },
          {
            /* Project Tasks: accessible to all roles including Collaborators */
            path: "/tasks/:projectId",
            element: <TaskViews />,
          },
          {
            /* Personal task execution: accessible to all */
            path: "/mytasks",
            element: <MyTasks />,
          },
          {
            /* Personal deliverable execution: accessible to all */
            path: "/my-deliverables",
            element: <MyDeliverables />,
          },

          {
            element: <RoleGuard allowedRoles={MANAGER_ROLES} redirectTo="/projects" />,
            children: [
              {
                path: "/cra-dashboard",
                element: <CraDashboard />,
              },
            ],
          },

          {
            element: <SubscriptionGuard feature="paid" />,
            children: [
              {
                /* Workload and Risk: restricted from Collaborators */
                element: <RoleGuard disallowedRoles={NON_COLLABORATOR_ROLES} redirectTo="/mytasks" />,
                children: [
                  {
                    path: "/workload/:projectId",
                    element: <TaskViews />,
                  },
                  {
                    path: "/deliverables/:projectId",
                    element: <DeliverablesList />,
                  },
                  {
                    path: "/deliverables/:projectId/create",
                    element: <CreateDeliverable />,
                  },
                  {
                    path: "/deliverables/detail/:deliverableId",
                    element: <DeliverableDetail />,
                  },
                  {
                    path: "/deliverables/:projectId/detail/:deliverableId",
                    element: <DeliverableDetail />,
                  },
                  {
                    path: "/risk/:projectId",
                    element: <RiskManagement />,
                  },
                ],
              },
              {
                /* Risk Scales: Admin and Managers only */
                element: <RoleGuard allowedRoles={MANAGER_ROLES} redirectTo="/projects" />,
                children: [
                  {
                    path: "/risk/scales",
                    element: <Scales />,
                  },
                ],
              },
            ],
          },

          {
            element: <SubscriptionGuard feature="timesheet" />,
            children: [
              {
                path: "/my-timesheet",
                element: <MyTimesheet />,
              },
            ],
          },

          {
            element: <RoleGuard allowedRoles={MANAGER_ROLES} redirectTo="/projects" />,
            children: [
              {
                element: <SubscriptionGuard feature="timesheet" />,
                children: [
                  {
                    path: "/timesheet-approvals",
                    element: <TimesheetApprovals />,
                  },
                ],
              },
            ],
          },

          {
            /* Project Budget: Managers and Admin only, plus Subscription check */
            element: <RoleGuard allowedRoles={MANAGER_ROLES} redirectTo="/projects" />,
            children: [
              {
                element: <SubscriptionGuard feature="budget" />,
                children: [
                  {
                    path: "/budget/:projectId",
                    element: <ProjectBudget />,
                  },
                  {
                    path: "/budget-performance",
                    element: <BudgetPerformance />,
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
],
},
  {
    path: "docs",
    element: <ApiDocumentation />,
    errorElement: <RouteErrorFallback />,
  },
  {
    path: "*",
    element: <Page404 />,
    errorElement: <RouteErrorFallback />,
  },
]);
